import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  loginUser, 
  registerUser, 
  requestPasswordReset, 
  confirmPasswordReset,
  lookupUsername 
} from './authService';

const AuthModal = ({ isOpen, onClose, initialTab = 'login', onAuthSuccess }) => {
  const history = useHistory();

  const [activeTab, setActiveTab] = useState(initialTab); // 'login' | 'register' | 'forgot' | 'forgot_username'
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Form states
  const [identifier, setIdentifier] = useState(''); // Email or Username
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');

  // Password reset specific
  const [resetStep, setResetStep] = useState(1); // 1 = request token, 2 = enter token & new password
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Forgot username specific
  const [lookupEmail, setLookupEmail] = useState('');
  const [foundAccount, setFoundAccount] = useState(null);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    const res = await loginUser(identifier, password);
    setLoading(false);

    if (res.success) {
      if (onAuthSuccess) onAuthSuccess(res.user);
      onClose();
      if (res.user.role === 'admin') {
        history.push('/admin');
      } else {
        history.push('/dashboard');
      }
    } else {
      setErrorMessage(res.error || 'Login failed. Please check your credentials.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    const res = await registerUser({ username, name, email, password, organization });
    setLoading(false);

    if (res.success) {
      if (onAuthSuccess) onAuthSuccess(res.user);
      onClose();
      if (res.user.role === 'admin') {
        history.push('/admin');
      } else {
        history.push('/dashboard');
      }
    } else {
      setErrorMessage(res.error || 'Registration failed.');
    }
  };

  const handleRequestToken = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const res = requestPasswordReset(resetIdentifier);
    if (res.success) {
      setResetStep(2);
      setSuccessMessage('A reset verification code has been dispatched. Enter the code below.');
    } else {
      setErrorMessage(res.error || 'Unable to request password reset.');
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword.length < 6) {
      setErrorMessage('New password must be at least 6 characters.');
      return;
    }

    const res = await confirmPasswordReset(resetIdentifier, resetToken, newPassword);
    if (res.success) {
      setSuccessMessage(res.message);
      setTimeout(() => {
        setIdentifier(resetIdentifier);
        setActiveTab('login');
        setResetStep(1);
        setSuccessMessage('');
        setPassword('');
      }, 1500);
    } else {
      setErrorMessage(res.error || 'Unable to reset password.');
    }
  };

  const handleLookupUsername = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setFoundAccount(null);

    const res = lookupUsername(lookupEmail);
    if (res.success) {
      setFoundAccount(res);
      setSuccessMessage(`Account found for ${res.name}!`);
    } else {
      setErrorMessage(res.error || 'No account found with this email address.');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(6px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        maxWidth: '480px',
        width: '100%',
        padding: '2.5rem',
        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
        position: 'relative',
        color: '#0f172a'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1.2rem',
            right: '1.2rem',
            background: 'transparent',
            border: 'none',
            fontSize: '1.5rem',
            color: '#94a3b8',
            cursor: 'pointer',
            lineHeight: 1
          }}
          aria-label="Close dialog"
        >
          ×
        </button>

        {/* Tab Headers */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1.8rem' }}>
          <button
            type="button"
            onClick={() => { setActiveTab('login'); setErrorMessage(''); setSuccessMessage(''); }}
            style={{
              flex: 1,
              padding: '0.6rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'login' ? '2px solid #1d4ed8' : 'none',
              fontWeight: activeTab === 'login' ? 800 : 600,
              color: activeTab === 'login' ? '#1d4ed8' : '#64748b',
              fontSize: '0.95rem',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('register'); setErrorMessage(''); setSuccessMessage(''); }}
            style={{
              flex: 1,
              padding: '0.6rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'register' ? '2px solid #1d4ed8' : 'none',
              fontWeight: activeTab === 'register' ? 800 : 600,
              color: activeTab === 'register' ? '#1d4ed8' : '#64748b',
              fontSize: '0.95rem',
              cursor: 'pointer'
            }}
          >
            Create Account
          </button>
        </div>

        {/* Alerts */}
        {errorMessage && (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#b91c1c', fontSize: '0.84rem', marginBottom: '1.2rem' }}>
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', color: '#15803d', fontSize: '0.84rem', marginBottom: '1.2rem' }}>
            {successMessage}
          </div>
        )}

        {/* 1. SIGN IN VIEW (Username or Email) */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', margin: 0 }}>
                  Username or Email Address
                </label>
                <button
                  type="button"
                  onClick={() => { setActiveTab('forgot_username'); setErrorMessage(''); setSuccessMessage(''); }}
                  style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Forgot Username?
                </button>
              </div>
              <input
                type="text"
                required
                className="workshop-input"
                placeholder="handle or you@company.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '1.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', margin: 0 }}>
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => { 
                    setResetIdentifier(identifier);
                    setActiveTab('forgot'); 
                    setErrorMessage(''); 
                    setSuccessMessage(''); 
                  }}
                  style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Forgot Password?
                </button>
              </div>
              <input
                type="password"
                required
                className="workshop-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-blue"
              style={{ width: '100%', justifyContent: 'center', padding: '0.85rem', fontSize: '0.96rem', fontWeight: 800 }}
            >
              {loading ? 'Authenticating...' : 'Sign In to Portal →'}
            </button>
          </form>
        )}

        {/* 2. CREATE ACCOUNT VIEW */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Full Name *
              </label>
              <input
                type="text"
                required
                className="workshop-input"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Username / Handle * (min 3 chars)
              </label>
              <input
                type="text"
                required
                className="workshop-input"
                placeholder="janedoe"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Work Email Address *
              </label>
              <input
                type="email"
                required
                className="workshop-input"
                placeholder="jane@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Company or Organization (Optional)
              </label>
              <input
                type="text"
                className="workshop-input"
                placeholder="Acme Corp or Personal Project"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '1.4rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                Create Password * (min 6 chars)
              </label>
              <input
                type="password"
                required
                className="workshop-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-blue"
              style={{ width: '100%', justifyContent: 'center', padding: '0.85rem', fontSize: '0.96rem', fontWeight: 800 }}
            >
              {loading ? 'Creating Account...' : 'Create Client Account →'}
            </button>
          </form>
        )}

        {/* 3. FORGOT USERNAME VIEW */}
        {activeTab === 'forgot_username' && (
          <div>
            <h4 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.4rem' }}>
              Retrieve Your Username
            </h4>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.2rem' }}>
              Enter the email address registered with your account to look up your username handle.
            </p>

            <form onSubmit={handleLookupUsername}>
              <div style={{ marginBottom: '1.2rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                  Registered Email Address
                </label>
                <input
                  type="email"
                  required
                  className="workshop-input"
                  placeholder="you@company.com"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                />
              </div>

              {foundAccount && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8fafc',
                  border: '1.5px solid #10b981',
                  borderRadius: '12px',
                  marginBottom: '1.2rem'
                }}>
                  <div style={{ fontSize: '0.78rem', color: '#059669', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>
                    ✓ Verified Account Match
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.2rem' }}>
                    Username: <span style={{ color: '#1d4ed8' }}>@{foundAccount.username}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Registered to: <strong>{foundAccount.name}</strong> ({foundAccount.maskedEmail})
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIdentifier(foundAccount.username);
                      setActiveTab('login');
                      setErrorMessage('');
                      setSuccessMessage('');
                    }}
                    className="btn-primary-blue"
                    style={{ marginTop: '0.8rem', width: '100%', justifyContent: 'center', padding: '0.6rem', fontSize: '0.88rem' }}
                  >
                    Sign In with @{foundAccount.username} →
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.8rem' }}>
                <button
                  type="button"
                  onClick={() => { setActiveTab('login'); setErrorMessage(''); setSuccessMessage(''); }}
                  className="btn-outline-blue"
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Back to Sign In
                </button>
                <button
                  type="submit"
                  className="btn-primary-blue"
                  style={{ flex: 2, justifyContent: 'center' }}
                >
                  Find My Username →
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 4. FORGOT PASSWORD FLOW */}
        {activeTab === 'forgot' && (
          <div>
            <h4 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.4rem' }}>
              Reset Your Password
            </h4>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.2rem' }}>
              Enter your registered username or email address.
            </p>

            {resetStep === 1 ? (
              <form onSubmit={handleRequestToken}>
                <div style={{ marginBottom: '1.2rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                    Username or Email Address
                  </label>
                  <input
                    type="text"
                    required
                    className="workshop-input"
                    placeholder="handle or you@company.com"
                    value={resetIdentifier}
                    onChange={(e) => setResetIdentifier(e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  <button
                    type="button"
                    onClick={() => { setActiveTab('login'); setErrorMessage(''); setSuccessMessage(''); }}
                    className="btn-outline-blue"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    Back to Sign In
                  </button>
                  <button
                    type="submit"
                    className="btn-primary-blue"
                    style={{ flex: 2, justifyContent: 'center' }}
                  >
                    Send Reset Code →
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmReset}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                    Security Reset Code
                  </label>
                  <input
                    type="text"
                    required
                    className="workshop-input"
                    placeholder="RESET-XXXXXX"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                  />
                </div>

                <div style={{ marginBottom: '1.4rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>
                    New Password (min 6 chars)
                  </label>
                  <input
                    type="password"
                    required
                    className="workshop-input"
                    placeholder="Enter new password (min 6 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  <button
                    type="button"
                    onClick={() => setResetStep(1)}
                    className="btn-outline-blue"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn-primary-blue"
                    style={{ flex: 2, justifyContent: 'center' }}
                  >
                    Update Password →
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
